import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { console_ } from '../services/api';
import { dateFr, dateHeureFr, n } from '../services/format';
import { Etat, Source, Erreur, Chargement, Vide, messageErreur } from './Ui';

// Personnes : recherche par téléphone / e-mail / nom → fiche d'un identifiant
// (consentements courants par société, historique + preuves, demandes,
// relations) et actions opérateur.
export default function Personnes({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [resultat, setResultat] = useState(null);
  const [liste, setListe] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [config, setConfig] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const peutAgir = user.role === 'admin' || user.role === 'operateur';

  useEffect(() => { console_.config().then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (id) chargerFiche(id);
    else { setFiche(null); console_.identifiants().then((r) => setListe(r.data)).catch((e) => setErreur(messageErreur(e))); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const chargerFiche = async (ident) => {
    setChargement(true); setErreur('');
    try { setFiche((await console_.fiche(ident)).data); } catch (e) { setErreur(messageErreur(e)); }
    setChargement(false);
  };

  const rechercher = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setChargement(true); setErreur(''); setResultat(null);
    try {
      const r = (await console_.rechercher(q.trim())).data;
      if (r.connu) navigate(`/personnes/${r.identifiant.id}`);
      else setResultat(r);
    } catch (e2) { setErreur(messageErreur(e2)); }
    setChargement(false);
  };

  return (
    <div>
      <h1 className="rg-titre-page">Personnes</h1>
      <form className="rg-recherche" onSubmit={rechercher}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Téléphone, e-mail ou nom" aria-label="Rechercher" autoFocus />
        <button className="rg-btn rg-btn--principal" type="submit" disabled={chargement}>Rechercher</button>
      </form>
      <Erreur message={erreur} />
      {resultat && !resultat.connu && (
        <div className="rg-bloc" style={{ marginBottom: 16 }}>
          {resultat.erreur ? <p>Identifiant non reconnu ({resultat.erreur}).</p> : resultat.valeur ? <p>Aucun consentement connu pour <strong>{resultat.valeur}</strong> ({resultat.type}). Une demande ne peut être envoyée qu'à un identifiant connu : passez par l'API ou l'import.</p> : null}
          {resultat.personnes?.length > 0 && (
            <table className="rg-tableau">
              <thead><tr><th>Nom</th><th>Identifiants</th></tr></thead>
              <tbody>
                {resultat.personnes.map((p) => (
                  <tr key={p.id}>
                    <td>{p.nom} {p.prenom}</td>
                    <td>{(p.identifiants || []).map((i) => <button key={i.id} className="rg-btn" style={{ marginRight: 6 }} onClick={() => navigate(`/personnes/${i.id}`)}>{i.valeur}</button>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {resultat.personnes?.length === 0 && !resultat.valeur && <p>Aucune personne trouvée.</p>}
        </div>
      )}
      {chargement && <Chargement />}
      {fiche && config && <Fiche fiche={fiche} config={config} peutAgir={peutAgir} recharger={() => chargerFiche(id)} />}
      {!id && liste && (
        <section className="rg-section">
          <h2>Derniers identifiants touchés<small>{liste.length} affichés</small></h2>
          {liste.length === 0 ? <Vide>Aucun identifiant pour l'instant : importez la base client ou enregistrez un premier consentement.</Vide> : (
            <div className="rg-defile">
              <table className="rg-tableau">
                <thead><tr><th>Identifiant</th><th>Personne</th><th className="rg-num">Accordés</th><th className="rg-num">Refus</th><th>Dernière demande</th><th>Dernier contact</th></tr></thead>
                <tbody>
                  {liste.map((i) => (
                    <tr key={i.id} className="cliquable" onClick={() => navigate(`/personnes/${i.id}`)}>
                      <td className="rg-mono">{i.valeur}</td>
                      <td>{[i.prenom, i.nom].filter(Boolean).join(' ') || <span className="rg-info">—</span>}</td>
                      <td className="rg-num">{n(i.accordes)}</td>
                      <td className="rg-num">{n(i.refus)}</td>
                      <td>{i.derniere_demande ? dateHeureFr(i.derniere_demande) : '—'}</td>
                      <td>{i.dernier_contact_le ? dateFr(i.dernier_contact_le) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Fiche({ fiche, config, peutAgir, recharger }) {
  const { identifiant, personne, autres_identifiants, relations, courants, historique, demandes } = fiche;
  const [panneau, setPanneau] = useState(null); // 'demander' | 'acte' | 'opposer' | 'personne'
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  const societes = config.societes.filter((s) => s.actif);
  const finalites = config.finalites.filter((f) => f.actif);
  const parSociete = {};
  for (const c of courants) { (parSociete[c.raison_sociale] = parSociete[c.raison_sociale] || []).push(c); }
  // Sociétés avec relation mais sans aucun consentement : affichées « jamais demandé ».
  for (const r of relations) if (!parSociete[r.raison_sociale]) parSociete[r.raison_sociale] = [];

  const fini = (msg) => { setPanneau(null); setMessage(msg); setErreur(''); recharger(); };
  const echec = (e) => setErreur(messageErreur(e));

  const basculerContrat = async (r) => {
    const actif = !r.contrat_en_cours;
    let justification = null;
    if (actif) {
      justification = window.prompt('Justification du contrat en cours (obligatoire, ex. : garantie constructeur jusqu\'au …) :');
      if (!justification) return;
    }
    try { await console_.relation(r.id, { contrat_en_cours: actif, justification }); recharger(); } catch (e) { echec(e); }
  };

  return (
    <div>
      {message && <div className="rg-succes">{message}</div>}
      <Erreur message={erreur} />
      <div className="rg-grille" style={{ marginBottom: 16 }}>
        <div className="rg-bloc">
          <h3>{identifiant.type === 'tel' ? 'Téléphone' : 'E-mail'} <span className="rg-mono" style={{ fontSize: 15 }}>{identifiant.valeur}</span></h3>
          <dl>
            <dt>Personne</dt><dd>{personne ? `${personne.prenom || ''} ${personne.nom || ''}`.trim() || '—' : <span className="rg-info">non rattachée</span>}</dd>
            {personne?.type && <><dt>Type</dt><dd>{personne.type}</dd></>}
            <dt>Dernier contact</dt><dd>{identifiant.dernier_contact_le ? dateFr(identifiant.dernier_contact_le) : '—'}</dd>
            <dt>Connu depuis</dt><dd>{dateFr(identifiant.cree_le)}</dd>
            {identifiant.type === 'tel' && <><dt>Mobile</dt><dd>{identifiant.mobile === false ? 'non (pas de SMS)' : 'oui'}</dd></>}
            {autres_identifiants.length > 0 && <><dt>Autres identifiants</dt><dd>{autres_identifiants.map((a) => <a key={a.id} href={`/personnes/${a.id}`} className="rg-mono" style={{ marginRight: 8 }}>{a.valeur}</a>)}</dd></>}
          </dl>
          {peutAgir && (
            <div className="rg-actions">
              <button className="rg-btn rg-btn--principal" onClick={() => setPanneau(panneau === 'demander' ? null : 'demander')}>Demander par {identifiant.type === 'tel' ? 'SMS' : 'e-mail'}</button>
              <button className="rg-btn" onClick={() => setPanneau(panneau === 'acte' ? null : 'acte')}>Enregistrer un recueil oral / papier</button>
              <button className="rg-btn rg-btn--danger" onClick={() => setPanneau(panneau === 'opposer' ? null : 'opposer')}>Opposition</button>
              <button className="rg-btn" onClick={() => setPanneau(panneau === 'personne' ? null : 'personne')}>Rattacher une personne</button>
            </div>
          )}
        </div>
        {panneau === 'demander' && <PanneauDemander identifiant={identifiant} personne={personne} societes={societes} finalites={finalites} onFini={fini} onErreur={echec} />}
        {panneau === 'acte' && <PanneauActe identifiant={identifiant} societes={societes} finalites={finalites} onFini={fini} onErreur={echec} />}
        {panneau === 'opposer' && <PanneauOpposer identifiant={identifiant} societes={societes} finalites={finalites} onFini={fini} onErreur={echec} />}
        {panneau === 'personne' && <PanneauPersonne identifiant={identifiant} personne={personne} onFini={fini} onErreur={echec} />}
      </div>

      <section className="rg-section">
        <h2>Consentements en cours<small>par société responsable</small></h2>
        {Object.keys(parSociete).length === 0 ? <Vide>Aucun consentement ni relation connus.</Vide> : (
          <div className="rg-grille">
            {Object.entries(parSociete).map(([soc, lignes]) => (
              <div className="rg-bloc" key={soc}>
                <h3>{soc}</h3>
                <dl>
                  {finalites.map((f) => {
                    const c = lignes.find((l) => l.finalite_code === f.code);
                    return (
                      <React.Fragment key={f.code}>
                        <dt title={f.libelle}>{f.code.replace('prospection_', 'prospection ')}</dt>
                        <dd><Etat etat={c ? c.etat_effectif : 'inconnu'} jusquau={c?.valide_jusqu_au} />{c?.base_legale && c.base_legale !== 'consentement' && <span className="rg-info" style={{ display: 'inline', marginLeft: 6 }}>({c.base_legale})</span>}</dd>
                      </React.Fragment>
                    );
                  })}
                </dl>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rg-section">
        <h2>Relations client<small>base « client existant »</small></h2>
        {relations.length === 0 ? <p className="rg-info">Aucune relation enregistrée.</p> : (
          <div className="rg-defile">
            <table className="rg-tableau">
              <thead><tr><th>Société</th><th>Relation</th><th>Dernière date</th><th>Source</th><th>Contrat en cours</th></tr></thead>
              <tbody>
                {relations.map((r) => (
                  <tr key={r.id}>
                    <td>{r.raison_sociale}</td>
                    <td>{r.relation}</td>
                    <td>{dateFr(r.derniere_date)}</td>
                    <td>{r.source}</td>
                    <td>
                      {r.contrat_en_cours ? <span className="rg-etat rg-etat--exception" title={r.justification || ''}>oui</span> : <span className="rg-info" style={{ display: 'inline' }}>non</span>}
                      {peutAgir && <button className="rg-btn" style={{ marginLeft: 8 }} onClick={() => basculerContrat(r)}>{r.contrat_en_cours ? 'Lever' : 'Déclarer'}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rg-section">
        <h2>Demandes envoyées<small>{demandes.length}</small></h2>
        {demandes.length === 0 ? <p className="rg-info">Aucune demande.</p> : (
          <div className="rg-defile">
            <table className="rg-tableau">
              <thead><tr><th>Date</th><th>Société</th><th>Canal</th><th>Finalités</th><th>Origine</th><th>Statut</th><th>Ouverte</th><th>Répondue</th><th>Expire</th></tr></thead>
              <tbody>
                {demandes.map((d) => (
                  <tr key={d.id}>
                    <td>{dateHeureFr(d.envoye_le)}</td>
                    <td>{d.societe_code}</td>
                    <td>{d.canal}{d.mode === 'simulation' ? ' (simulation)' : ''}</td>
                    <td>{(d.finalites || []).map((f) => f.replace('prospection_', '')).join(', ')}</td>
                    <td>{d.origine}{d.relance_n ? ` · relance ${d.relance_n}` : ''}</td>
                    <td>{d.statut}</td>
                    <td>{d.ouvert_le ? dateHeureFr(d.ouvert_le) : '—'}</td>
                    <td>{d.repondu_le ? dateHeureFr(d.repondu_le) : '—'}</td>
                    <td>{dateFr(d.expire_le)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rg-section">
        <h2>Historique et preuves<small>{historique.length} événements, en ajout seul</small></h2>
        {historique.length === 0 ? <p className="rg-info">Aucun événement.</p> : (
          <div className="rg-defile">
            <table className="rg-tableau">
              <thead><tr><th>Horodatage</th><th>Société</th><th>Finalité</th><th>État</th><th>Base légale</th><th>Source</th><th>Acteur</th><th>Mention</th><th>Preuve</th></tr></thead>
              <tbody>
                {historique.map((h) => (
                  <tr key={h.id}>
                    <td>{dateHeureFr(h.collecte_le)}</td>
                    <td>{h.societe_code}</td>
                    <td>{h.finalite_code}</td>
                    <td><Etat etat={h.etat} jusquau={h.etat === 'accorde' ? h.valide_jusqu_au : null} /></td>
                    <td>{h.base_legale}</td>
                    <td><Source code={h.source} /></td>
                    <td>{h.acteur || '—'}</td>
                    <td>{h.mention_version ? `v${h.mention_version}` : '—'}</td>
                    <td>{h.token_preuve ? <a href={`/p/${h.token_preuve}`} target="_blank" rel="noreferrer" className="rg-mono" title={h.sha256}>{String(h.sha256 || '').slice(0, 10)}…</a> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ChoixSociete({ societes, valeur, onChange, optionnel }) {
  return (
    <label>Société responsable
      <select value={valeur} onChange={(e) => onChange(e.target.value)} required={!optionnel}>
        {optionnel && <option value="">Toutes</option>}
        {societes.map((s) => <option key={s.id} value={s.code}>{s.raison_sociale}</option>)}
      </select>
    </label>
  );
}

function PanneauDemander({ identifiant, personne, societes, finalites, onFini, onErreur }) {
  const [societe, setSociete] = useState(societes[0]?.code || '');
  const [choisies, setChoisies] = useState(finalites.map((f) => f.code));
  const [mode, setMode] = useState('actif');
  const [prenom, setPrenom] = useState(personne?.prenom || '');
  const [envoi, setEnvoi] = useState(false);
  const [lien, setLien] = useState('');
  const basculer = (c) => setChoisies((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]));
  const envoyer = async (e) => {
    e.preventDefault(); setEnvoi(true);
    try {
      const r = (await console_.demander(identifiant.id, { societe, finalites: choisies, canal: 'auto', mode, prenom })).data;
      setLien(r.lien);
      onFini(`Demande ${mode === 'simulation' ? 'simulée' : 'envoyée'} : ${r.lien}`);
    } catch (e2) { onErreur(e2); }
    setEnvoi(false);
  };
  return (
    <form className="rg-bloc rg-form" onSubmit={envoyer}>
      <h3>Demander le consentement par {identifiant.type === 'tel' ? 'SMS' : 'e-mail'}</h3>
      <ChoixSociete societes={societes} valeur={societe} onChange={setSociete} />
      <div className="rg-cases">{finalites.map((f) => <label key={f.code}><input type="checkbox" checked={choisies.includes(f.code)} onChange={() => basculer(f.code)} /> {f.code.replace('prospection_', '')}</label>)}</div>
      <div className="rg-ligne">
        <label>Prénom (personnalisation)<input value={prenom} onChange={(e) => setPrenom(e.target.value)} /></label>
        <label>Mode<select value={mode} onChange={(e) => setMode(e.target.value)}><option value="actif">Envoi réel</option><option value="simulation">Simulation</option></select></label>
      </div>
      <p className="rg-info">Une demande de moins de 10 jours déjà en cours est refusée (anti-harcèlement).</p>
      {lien && <p className="rg-mono">{lien}</p>}
      <div className="rg-actions"><button className="rg-btn rg-btn--principal" disabled={envoi || !choisies.length}>Envoyer</button></div>
    </form>
  );
}

function PanneauActe({ identifiant, societes, finalites, onFini, onErreur }) {
  const [societe, setSociete] = useState(societes[0]?.code || '');
  const [choix, setChoix] = useState(Object.fromEntries(finalites.map((f) => [f.code, null])));
  const [source, setSource] = useState('oral_appel');
  const [reference, setReference] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const valider = async (e) => {
    e.preventDefault(); setEnvoi(true);
    const retenu = Object.fromEntries(Object.entries(choix).filter(([, v]) => v !== null));
    try { await console_.acte(identifiant.id, { societe, choix: retenu, source, reference }); onFini('Recueil enregistré, preuve créée.'); } catch (e2) { onErreur(e2); }
    setEnvoi(false);
  };
  const nb = Object.values(choix).filter((v) => v !== null).length;
  return (
    <form className="rg-bloc rg-form" onSubmit={valider}>
      <h3>Enregistrer un recueil fait ailleurs</h3>
      <p className="rg-info">Le texte des mentions en vigueur est figé dans la preuve : lisez-le au client avant de cocher.</p>
      <ChoixSociete societes={societes} valeur={societe} onChange={setSociete} />
      <div className="rg-ligne">
        <label>Source<select value={source} onChange={(e) => setSource(e.target.value)}><option value="oral_appel">Oral, pendant un appel</option><option value="papier">Papier signé</option><option value="operateur">Autre (opérateur)</option></select></label>
        <label>Référence (n° d'appel, OR, document)<input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
      </div>
      <table className="rg-tableau">
        <tbody>
          {finalites.map((f) => (
            <tr key={f.code}>
              <td>{f.libelle}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <label style={{ marginRight: 12 }}><input type="radio" name={f.code} checked={choix[f.code] === true} onChange={() => setChoix({ ...choix, [f.code]: true })} /> Accepte</label>
                <label style={{ marginRight: 12 }}><input type="radio" name={f.code} checked={choix[f.code] === false} onChange={() => setChoix({ ...choix, [f.code]: false })} /> Refuse</label>
                <label><input type="radio" name={f.code} checked={choix[f.code] === null} onChange={() => setChoix({ ...choix, [f.code]: null })} /> Non abordé</label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="rg-actions"><button className="rg-btn rg-btn--principal" disabled={envoi || nb === 0}>Enregistrer ({nb})</button></div>
    </form>
  );
}

function PanneauOpposer({ identifiant, societes, finalites, onFini, onErreur }) {
  const [societe, setSociete] = useState('');
  const [choisies, setChoisies] = useState([]);
  const [reference, setReference] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const basculer = (c) => setChoisies((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]));
  const valider = async (e) => {
    e.preventDefault(); setEnvoi(true);
    try {
      const r = (await console_.opposer(identifiant.id, { societe: societe || undefined, finalites: choisies.length ? choisies : undefined, source: 'operateur', reference })).data;
      onFini(`Opposition enregistrée (${r.actes} retrait${r.actes > 1 ? 's' : ''}).`);
    } catch (e2) { onErreur(e2); }
    setEnvoi(false);
  };
  return (
    <form className="rg-bloc rg-form" onSubmit={valider}>
      <h3>Opposition (droit absolu, sans justification)</h3>
      <ChoixSociete societes={societes} valeur={societe} onChange={setSociete} optionnel />
      <div className="rg-cases">{finalites.map((f) => <label key={f.code}><input type="checkbox" checked={choisies.includes(f.code)} onChange={() => basculer(f.code)} /> {f.code.replace('prospection_', '')}</label>)}</div>
      <p className="rg-info">Aucune finalité cochée = toutes. Chaque retrait crée une preuve.</p>
      <label>Référence (appel, courrier…)<input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
      <div className="rg-actions"><button className="rg-btn rg-btn--danger" disabled={envoi}>Enregistrer l'opposition</button></div>
    </form>
  );
}

function PanneauPersonne({ identifiant, personne, onFini, onErreur }) {
  const [nom, setNom] = useState(personne?.nom || '');
  const [prenom, setPrenom] = useState(personne?.prenom || '');
  const [type, setType] = useState(personne?.type || 'particulier');
  const valider = async (e) => {
    e.preventDefault();
    try { await console_.personne(identifiant.id, { nom, prenom, type }); onFini('Personne rattachée.'); } catch (e2) { onErreur(e2); }
  };
  return (
    <form className="rg-bloc rg-form" onSubmit={valider}>
      <h3>Rattacher une personne</h3>
      <div className="rg-ligne">
        <label>Nom<input value={nom} onChange={(e) => setNom(e.target.value)} /></label>
        <label>Prénom<input value={prenom} onChange={(e) => setPrenom(e.target.value)} /></label>
        <label>Type<select value={type} onChange={(e) => setType(e.target.value)}><option value="particulier">Particulier</option><option value="professionnel">Professionnel</option></select></label>
      </div>
      <div className="rg-actions"><button className="rg-btn rg-btn--principal">Enregistrer</button></div>
    </form>
  );
}
